import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import useGetData from 'hooks/useGetData'
import useSaga from 'hooks/useSaga'
import { FormattedMessage } from 'react-intl'
import {
  ListItem
} from 'components/List'
import KanBoIcon from 'components/KanBoIcon'
import Loader from 'components/Loader'
import Tabs from 'components/Tabs'
import {
  LeftSidebarView,
} from 'components/Sidebar'
import Scrollbars from 'components/Scrollbars'
import withLeftSidebar from 'components/Sidebar/withLeftSidebar'
import HiddenFileInput from 'components/HiddenFileInput'
import desksChannelData from './desksChannelData'
import classes from './Desks.module.scss'
import AddDesk from './AddDesk'
import LayoutSettings from '../LayoutSettings'
import { useHistory } from 'react-router'

export function Desks ({
  leftSidebar,

  currentUserRoles,
  currentUserId,

  requestAddLayoutTemplateFromFile,
}) {
  const history = useHistory()

  const [selectedTabId, setSelectedTabId] = useState('normal')

  const isTemplate = useMemo(() => {
    return selectedTabId === 'template'
  }, [selectedTabId])

  const [layouts, setLayouts] = useState([])

  const args = useMemo(() => ({
    limit: 10000,
    isTemplate
  }), [isTemplate])
  const {
    data: LayoutsData,
    isLoading
  } = useGetData('HomeLayouts', args)

  useEffect(() => {
    if (LayoutsData) {
      setLayouts(LayoutsData.Layouts.map(Layout => ({
        id: Layout.Id,
        name: Layout.Name,
        description: Layout.Description,
        isPublic: Layout.IsPublic,
        isShared: Layout.IsShared,
        isTemplate: Layout.IsTemplate,
        isLocked: Layout.IsLocked,
      })))
    } else {
      setLayouts([])
    }
  }, [LayoutsData])

  const handleChannelDataRef = useRef(null)

  useEffect(() => {
    handleChannelDataRef.current = ({ channel, actionName, elementName, data }) => {
      if (channel === '@' + currentUserId) {
        if (actionName + elementName === 'AddHomeLayout/Data/HomeLayout') {
          const Layout = data

          if (Layout.IsTemplate === isTemplate) {
            setLayouts([
              {
                id: Layout.Id,
                name: Layout.Name,
                description: Layout.Description,
                isPublic: Layout.IsPublic,
                isShared: Layout.IsShared,
                isTemplate: Layout.IsTemplate,
                isLocked: Layout.IsLocked,
              },
              ...layouts.filter(layout => layout.id !== Layout.Id)
            ].sort((a, b) => {
              const textA = a.name.toUpperCase()
              const textB = b.name.toUpperCase()
              return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
            }))
          }
        } else if (actionName + elementName === 'UnlockHomeLayout/Primitive/Int32') {
          const { Value } = data

          const index = layouts.findIndex(layout => layout.id === Value)

          if (index !== -1) {
            setLayouts([
              ...layouts.slice(0, index),
              {
                ...layouts[index],
                isLocked: false,
              },
              ...layouts.slice(index + 1)
            ])
          }
        }
      } else if (channel === 'landingPage') {
        if (actionName + elementName === 'RemoveHomeLayout/Primitive/MaybeInt32') {
          const { Value } = data

          setLayouts(layouts.filter(layout => layout.id !== Value))
        }
      }
    }
  }, [layouts, currentUserId, isTemplate])

  const handleChannelData = useCallback(
    (...args) => {
      handleChannelDataRef.current && handleChannelDataRef.current(...args)
    },
    []
  )

  useSaga(desksChannelData, handleChannelData)

  const handleAddClick = useCallback(() => {
    leftSidebar.push(() => <AddDesk isTemplate={isTemplate} />)
  }, [leftSidebar, isTemplate])

  const handleFileInputChange = useCallback((event) => {
    const files = event.target.files
    const count = files.length

    if (count > 0) {
      const file = files[0]

      let name = file.name

      const index = name.lastIndexOf('.')
      if (index >= 0) {
        name = name.substr(0, index)
      }

      requestAddLayoutTemplateFromFile({
        file,
        name,
      })
    }
  }, [requestAddLayoutTemplateFromFile])

  return (
    <LeftSidebarView
      title={<FormattedMessage id='DESKS_MANAGE' />}
      buttons={(() => {
        const buttons = [[{
          icon: <KanBoIcon name='kanbo-16-plus' />,
          title: <FormattedMessage id='ADD' />,
          onClick: handleAddClick
        }]]

        if (isTemplate && currentUserRoles['templates']) {
          buttons[0].push({
            componentClass: 'div',
            icon: <KanBoIcon name='kanbo-16-plus' />,
            title: (
              <span>
                <FormattedMessage id='DESK_ADD_NEW_TEMPLATE_FROM_FILE' />
                <HiddenFileInput
                  accept='.kbt'
                  onChange={handleFileInputChange}
                />
              </span>
            ),
            style: {
              position: 'relative',
              overflow: 'hidden'
            }
          })
        }

        return buttons[0].length > 0 ? buttons : undefined
      })()}
    >
      {currentUserRoles['templates'] &&
        <Tabs
          selectedTabId={selectedTabId}
          onTabChange={setSelectedTabId}
          tabs={[{
            id: 'normal',
            title: <FormattedMessage id='DESKS' />,
          }, {
            id: 'template',
            title: <FormattedMessage id='DESKS_TEMPLATE' />,
          }]}
          className={classes.tabs}
          tabContainerProps={{
            style: {
              padding: '0 10px'
            }
          }}
        />
      }
      <Scrollbars>
        {layouts.map(layout =>
          layout.isLocked ? (
            <ListItem
              key={layout.id}
              icon={<KanBoIcon spin name='kanbo-16-loading' style={{ fontSize: 24 }} />}
            >
              {layout.name}
            </ListItem>
          ) : (
            <ListItem
              key={layout.id}
              icon={
                <KanBoIcon
                  name={
                    layout.isPublic
                      ? 'kanbo-24-desk-public-2'
                      : layout.isShared
                        ? 'kanbo-24-desk-shared'
                        : 'kanbo-24-desk'
                  }
                  style={{ fontSize: 24 }}
                />
              }
              subtitle={layout.description}
              menu={[{
                icon: <KanBoIcon name='kanbo-16-info' />,
                title: <FormattedMessage id='DESK_SETTINGS' />,
                onClick: () => {
                  leftSidebar.push(() => <LayoutSettings layoutId={layout.id} />)
                }
              }]}
              onClick={() => {
                history.push(`/home/${layout.id}`)

                leftSidebar.close()
              }}
            >
              {layout.name}
            </ListItem>
          )
        )
        }
        {isLoading &&
          <Loader center />
        }
      </Scrollbars>
    </LeftSidebarView>
  )
}

export default withLeftSidebar(Desks)
